<template>
    <sidebar @close="$emit('close')" ref="sidebar">
        <template v-slot:title v-if="employee">
            {{ employee.full_name }}
        </template>

        <template v-slot:subtitle v-if="employee">
            <span class="counter counter-warnings" v-if="warningCount">
                <i class="counter-icon fas fa-fw fa-exclamation-triangle" aria-label="Warnings"></i>
                <span class="counter-label">{{ warningCount }}</span>
            </span>
        </template>

        <template v-slot:header v-if="employee">
            <div class="row mb-1">
                <div class="col">
                    <span class="text-muted" :class="{ 'company-flash': flashCompany }">{{ employee.company.name }}</span>
                </div>
            </div>

            <div class="row mb-1">
                <div class="col">
                    <!-- Type of object -->
                    <span class="text-muted" v-if="employeeId">
                        Employee&nbsp;&middot;
                    </span>
                    <span class="text-muted" v-if="onboardingApplicationId">
                        Onboarding&nbsp;&middot;
                    </span>
                    <!-- Status -->
                    <span class="text-muted" v-if="isEmployee && employee.employment_status == 'Active'">
                        {{ employee.employment_status }}
                    </span>
                    <span class="text-danger-dark text-bold text-italic" v-if="isEmployee && ['Inactive', 'Terminated'].includes(employee.employment_status)">
                        {{ employee.employment_status }}
                    </span>
                    <span class="text-muted" v-if="onboardingApplicationId && employee.status != 'canceled'">
                        {{ employee.status_pretty }}
                    </span>
                    <span class="text-danger-dark text-bold text-italic" v-if="onboardingApplicationId && employee.status == 'canceled'">
                        {{ employee.status_pretty }}
                    </span>
                    <!-- Additional info if this is an employee -->
                    <span class="text-muted" v-if="isEmployee">
                        &nbsp;&middot; Employee Number {{ employee.employee_number }}
                    </span>
                    <div class="text-muted" v-if="isEmployee && employee.client_unique_clocking_id && employee.company.show_clocking_id">
                        Clocking ID {{ employee.client_unique_clocking_id }}
                    </div>
                </div>
            </div>

            <unlock-widget v-if="employee.user && employee.user.id && employee.is_locked" :client="client" :user="employee.user" :is-locked="employee.is_locked" @unlocked="getData"/>

            <OtherCompanyRecordsWidget
                v-if="isEmployee"
                :records="otherCompanyRecords"
                :expanded="otherCompanyRecordsExpanded"
                @toggle_expand="otherCompanyRecordsExpanded = !otherCompanyRecordsExpanded"
                @switch-employee="$emit('switch-employee', $event)"
            />

            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabContentMain" id="tabLinkMain" data-toggle="tab" role="tab" aria-controls="tabContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>

                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'misc'}" href="#tabContentMiscHr" id="tabLinkMiscHr" data-toggle="tab" role="tab" aria-controls="tabContentMiscHr" :aria-selected="activeTab == 'misc'">Misc HR</a>
                        </li>

                        <li v-if="!client.aos_client" class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'payroll'}" href="#tabContentPayroll" id="tabLinkPayroll" data-toggle="tab" role="tab" aria-controls="tabContentPayroll" :aria-selected="activeTab == 'payroll'">Payroll</a>
                        </li>

                        <li class="nav-item">
                            <a class="nav-link" v-if="isEmployee" :class="{active: activeTab == 'hr-docs'}" href="#tabContentHrDocs" id="tabLinkHrDocs" data-toggle="tab" role="tab" aria-controls="tabContentHrDocs" :aria-selected="activeTab == 'hr-docs'">HR Docs</a>
                        </li>

                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'warnings'}" href="#tabContentWarnings" id="tabLinkWarnings" data-toggle="tab" role="tab" aria-controls="tabContentWarnings" :aria-selected="activeTab == 'warnings'">Warnings</a>
                        </li>

                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'access'}" href="#tabUserAccess" id="tabLinkUserAccess" data-toggle="tab" role="tab" aria-controls="tabUserAccess" :aria-selected="activeTab == 'access'">User Access</a>
                        </li>

                        <li class="nav-item" v-if="isEmployee && showPOSTab">
                            <a class="nav-link" href="#tabContentPOS" id="tabLinkPOS" data-toggle="tab" role="tab" aria-controls="tabContentPOS" aria-selected="false">POS</a>
                        </li>

                        <li class="nav-item" v-if="$permissions.hasRolesInCompany(client.id, employee.company_id, $store.state.security_roles.CHANGE_HISTORY_ROLES)">
                            <a class="nav-link" :class="{active: activeTab == 'changelogs'}" href="#tabContentHistory" id="tabLinkHistory" data-toggle="tab" role="tab" aria-controls="tabContentHistory" :aria-selected="activeTab == 'changelogs'">History</a>
                        </li>

                        <li class="nav-item" v-if="userIsAdmin">
                            <a class="nav-link" href="#tabContentAdmin" id="tabLinkAdmin" data-toggle="tab" role="tab" aria-controls="tabContentAdmin" aria-selected="false">Admin</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot v-if="employee">
            <div class="tab-content flex-body flex-container">

                <div class="tab-pane flex-body" :class="{'show active': activeTab == 'main'}" id="tabContentMain" role="tabpanel" aria-labelledby="tabLinkMain">

                    <!-- "Scarlet Letter" indicator -->
                    <alert-box type="lockedWarning" v-if="i9CompletedLateWarnings.length">
                        <p class="mb-0">
                            <strong>I-9 Completed Late</strong>
                        </p>
                        <p class="mb-0 mt-1" v-for="w in i9CompletedLateWarnings" :key=w.id>
                            This employee's Form I-9 was completed late for their start date of {{ $filters.date(w.date) }}.
                        </p>
                    </alert-box>

                    <AOSEmployeeDetails
                        v-if="client.aos_client"
                        :client="client"
                        :employee="employee"
                        :can-edit="onboardingApplicationId && canEditAOSHRInfo && !employee.hr_info_approved_on"
                        @edit="openEditEmployeeModal"
                        @edit-email="openEditEmailModal"
                        @action-terminate="actionTerminate"
                    />

                    <EmployeeInfoWidget
                        v-if="!client.aos_client"
                        :client="client"
                        :employee="employee"
                        @updated="onUpdate"
                    />

                    <EmployeeContactInfoWidget
                        v-if="!client.aos_client"
                        :client="client"
                        :employee="employee"
                        @updated="onUpdate"
                    />

                    <EmployeeACAWidget
                        v-if="!client.aos_client"
                        :client="client"
                        :employee="employee"
                        @updated="onUpdate"
                    />

                    <p class="color-red" v-if="activeOnboardingCount">{{ activeOnboardingCount }} onboarding applications in progress. <router-link :to="{name: 'onboarding', params: {hashedClientId: $hasher.encode(client.id)}}">Go to Onboarding</router-link></p>
                </div>

                <div class="tab-pane flex-body" :class="{'show active': activeTab == 'hr-docs'}" id="tabContentHrDocs" role="tabpanel" aria-labelledby="tabLinkHrDocs">
                    <HRDocumentsTab
                        v-if="isEmployee"
                        :employee="employee"
                        :client="client"
                        :employeeDocuments="employeeDocuments"
                        :employeeDocumentTasks="employeeDocumentTasks"
                        :section="section"
                        :itemId="itemId"
                        @updated="onUpdate"
                        @doc-task-canceled="$emit('doc-task-canceled', $event)"
                        @employee-document-removed="$emit('employee-document-removed', $event)"
                    />
                </div>

                <div class="tab-pane flex-body" :class="{'show active': activeTab == 'payroll'}" id="tabContentPayroll" role="tabpanel" aria-labelledby="tabLinkPayroll">
                    <JobsWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client-earning-types="clientEarningTypes"
                        :positions="positions"
                        :client="client"
                        :section="section"
                        :itemId="itemId"
                        @updated="onUpdate"
                    />

                    <DeferredCompWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                        @updated="onUpdate"
                    />
                    <OtherBenefitDeductionsWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                        :client-deduction-types="otherBenefitClientDeductionTypes"
                        @updated="onUpdate"
                    />
                    <MiscDeductionsWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                        :client-deduction-types="miscClientDeductionTypes"
                        @updated="onUpdate"
                    />
                    <RetirementLoansWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                        @updated="onUpdate"
                    />
                    <IWOWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                        @updated="onUpdate"
                    />
                    <TaxWithholdingsWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                    />

                    <insurance-plans-widget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                        :section="section"
                        :itemId="itemId"
                        @updated="onUpdate"
                    />

                    <TimeOffWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                    />

                    <EarningsStatementHistoryWidget
                        v-if="isEmployee && !client.aos_client"
                        :employee="employee"
                        :client="client"
                        :checks="checks"
                        :activeCheckId="activeCheckId"
                        :expanded="checksExpanded"
                        :earning-types="clientEarningTypes"
                        @toggle_expand="checksExpanded = !checksExpanded"
                    />

                </div>

                <div class="tab-pane flex-body" :class="{'show active': activeTab == 'misc'}" id="tabContentMiscHr" role="tabpanel" aria-labelledby="tabLinkMiscHr">
                    <approval
                        v-if="approvalMode"
                        :onboarding-application="employee"
                        :client="client"
                        @close="approvalMode = false"
                        @updated="onUpdate"
                        @edit="openEditEmployeeModal"
                        @edit-email="openEditEmailModal"
                    />

                    <div v-show="!approvalMode">
                        <p class="text-right text-small">
                            <a href @click.prevent="expandAllMiscHr()">Expand all</a>
                            <span class="text-muted px-1">|</span>
                            <a href @click.prevent="collapseAllMiscHr()">Collapse all</a>
                        </p>

                        <OnboardingWidget
                            :employee="employee"
                            :client="client"
                            :onboardingApplicationId="onboardingApplicationId"
                            :onboardingApplications="onboardingApplications"
                            :expanded="onboardingAppsExpanded"
                            @resend-activation-email="resendActivationEmail"
                            @edit-email="openEditEmailModal"
                            @start-approval="approvalMode = true"
                            @toggle_expand="onboardingAppsExpanded = !onboardingAppsExpanded"
                        />

                        <ManagersWidget
                            :employee="employee"
                            :client="client"
                            :managers="managers"
                            :expanded="managersExpanded"
                            @toggle_expand="managersExpanded = !managersExpanded"
                        />

                        <EmergencyContactsWidget
                            v-if="managers"
                            :employee="employee"
                            :client="client"
                            :emergencyContacts="emergencyContacts"
                            :expanded="contactsExpanded"
                            @toggle_expand="contactsExpanded = !contactsExpanded"
                            @updated="onEmergencyContactsUpdate"
                        />

                        <CheckHistoryWidget
                            v-if="client.aos_client"
                            :employee="employee"
                            :client="client"
                            :checks="checks"
                            :activeCheckId="activeCheckId"
                            :expanded="checksExpanded"
                            @toggle_expand="checksExpanded = !checksExpanded"
                        />

                        <TaxDocumentsWidget
                            :employee="employee"
                            :client="client"
                            :taxForms="taxForms"
                            :activeTaxDocumentId="activeTaxDocumentId"
                            :expanded="taxDocsExpanded"
                            @toggle_expand="taxDocsExpanded = !taxDocsExpanded"
                        />

                        <EVerifyWidget
                            v-if="showEVerifyWidget"
                            :employee="employee"
                            :client="client"
                            :eVerifyCases="eVerifyCases"
                            :onboardingApplications="onboardingApplications"
                            :expanded="eVerifyExpanded"
                            @toggle_expand="eVerifyExpanded = !eVerifyExpanded"
                            @updated="onUpdate"
                        />
                    </div>
                </div>

                <!-- WARNINGS Tab Pane -->
                <div class="tab-pane flex-body" :class="{'show active': activeTab == 'warnings'}" id="tabContentWarnings" role="tabpanel" aria-labelledby="tabLinkWarnings">
                    <section class="table-section">
                        <warnings-header category="Dismissed" :expanded="dismissedWarningsExpanded" :warning-count="dismissedWarnings.length" @click="dismissedWarningsExpanded = !dismissedWarningsExpanded" />
                        <warnings :warnings="dismissedWarnings" :active-warning-id="activeWarningId" v-if="employee" v-show="dismissedWarningsExpanded" @updated="onWarningsUpdate" />
                    </section>
                    <section class="table-section">
                        <warnings-header category="Unresolved" :expanded="unresolvedWarningsExpanded" :warning-count="unresolvedWarnings.length" @click="unresolvedWarningsExpanded = !unresolvedWarningsExpanded" />
                        <warnings :warnings="unresolvedWarnings" :active-warning-id="activeWarningId" v-if="employee" v-show="unresolvedWarningsExpanded" @updated="onWarningsUpdate" />
                    </section>
                    <section class="table-section">
                        <warnings-header category="Resolved" :expanded="resolvedWarningsExpanded" :warning-count="resolvedWarnings.length" @click="resolvedWarningsExpanded = !resolvedWarningsExpanded" />
                        <warnings :warnings="resolvedWarnings" :active-warning-id="activeWarningId" v-if="employee" v-show="resolvedWarningsExpanded" @updated="onWarningsUpdate" />
                    </section>
                </div>

                <!-- USER ACCESS Tab Pane -->
                <div class="tab-pane flex-body" :class="{'show active': activeTab == 'access'}" id="tabUserAccess" role="tabpanel" aria-labelledby="tabLinkUserAccess">
                    <user-access :client="client" :user="employee.user" :email="employee.email" :login-sessions="loginSessions" :is-locked="employee.is_locked" :allow-security-profile-pin-reset="false" @pin_reset="resetPIN()" @send_invite_email="resendActivationEmail(user.email)" v-if="employee">
                        <template v-slot:pin-reset-disabled-message>
                            <p class="mb-0 pt-1">
                                This user account has Security User access. To reset their PIN, go to <strong>Settings &gt; Security Users</strong>.
                            </p>
                        </template>
                    </user-access>

                    <pin-reset-modal :user="employee.user" :client="client" v-if="employee.user && employee.user.id" ref="pinResetModal" />
                </div>

                <!-- POS ACTIONS Tab Pane -->
                <div class="tab-pane flex-body" id="tabContentPOS" role="tabpanel" aria-labelledby="tabLinkPOS" v-if="isEmployee && showPOSTab">
                    <div class="tab-pane pb-2" role="tabpanel">

                        <div class="card mb-2" v-if="showPosCard('lc_alt_api')">
                            <div class="card-header pr-1 d-flex align-items-center">
                                <span class="btnlike mr-auto">
                                    <h4 class="mb-0">Altametrics</h4>
                                </span>
                            </div>

                            <div class="card-body">
                                <div v-if="isEmployee" class="mb-2">
                                    <div class="flex-grow-0 flex-shrink-0 mr-1">
                                        <p>
                                            <span class="d-block text-semibold">
                                                GEID
                                            </span>
                                            <span class="d-block">
                                                {{ employee.external_pos_id }}
                                            </span>
                                        </p>
                                        <br>

                                        <button class="btn btn-outline-primary btn-block mb-3" @click.prevent="POSActionHire()" v-if="employee.employment_status == 'Active' || $permissions.hasRolesInCompany(client.id, employee.company_id, $store.state.security_roles.POS_SIDEBAR_ROLES)">POS Rehire</button>

                                        <button class="btn btn-outline-primary btn-block mb-3 disabled" @click.prevent="POSActionHireNotAllowed()" v-if="! (employee.employment_status == 'Active' || $permissions.hasRolesInCompany(client.id, employee.company_id, $store.state.security_roles.POS_SIDEBAR_ROLES))">POS Rehire</button>

                                        <button class="btn btn-outline-primary btn-block mb-3" @click.prevent="POSActionUpdateGeneral()">POS Update General Info</button>

                                        <button class="btn btn-outline-primary btn-block mb-3" @click.prevent="POSActionUpdateJobPay()">POS Update Job/Rate</button>

                                        <button class="btn btn-outline-primary btn-block mb-3" @click.prevent="POSActionTerminate()">POS Terminate</button>

                                        <hr>

                                        <div v-if="altametricsDataUpdates.length === 0">
                                            <p class="py-1 m-0 text-muted">
                                                <em>No Altametrics history recorded.</em>
                                            </p>
                                        </div>
                                        <div v-if="altametricsDataUpdates.length > 0">
                                            <ul class="list-unstyled m-0 text-small">
                                                <li class="d-none d-sm-block">
                                                    <div class="form-row align-items-baseline py-hf">
                                                        <div class="col-5">
                                                            <h5 class="theading m-0">Date & Time</h5>
                                                        </div>
                                                        <div class="col-4">
                                                            <h5 class="theading m-0">Action</h5>
                                                        </div>
                                                        <div class="col-3">
                                                            <h5 class="theading m-0">Status</h5>
                                                        </div>
                                                    </div>
                                                </li>

                                                <li class="boxed-li-bottom" v-for="updateObj in altametricsDataUpdates" :key="updateObj.id">
                                                    <div class="form-row align-items-baseline py-hf">
                                                        <div class="col-12 col-sm-5">
                                                            <span>
                                                                {{ $filters.datetimeshort(updateObj.created_on) }}
                                                            </span>
                                                        </div>
                                                        <div class="col-12 col-sm-4">
                                                            <span>{{ updateObj.action_pretty }} </span>
                                                        </div>
                                                        <div class="col-12 col-sm-3">
                                                            <a href @click.prevent="posDataUpdateObj = updateObj; POSDataStatusUpdateModalOpen = true;">{{ updateObj.status_pretty }} </a>
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card mb-2" v-if="showPosCard('tcpplus')">
                            <div class="card-header pr-1 d-flex align-items-center">
                                <span class="btnlike mr-auto">
                                    <h4 class="mb-0">TCP+</h4>
                                </span>
                            </div>
                            <div class="card-body">
                                <p class="text-muted">
                                    Status:
                                    <span v-if="employee.is_synced_to_pos">Synced on {{ employee.synced_to_pos_on }}</span>
                                    <span v-else>Not yet synced</span>
                                </p>
                                <button class="btn btn-outline-primary btn-block mb-2" :disabled="tcpplusSyncing || tcpplusTerminating" @click.prevent="syncToTCPPlus">
                                    {{ tcpplusSyncing ? 'Syncing…' : (employee.is_synced_to_pos ? 'Re-sync to TCP+' : 'Sync to TCP+') }}
                                </button>
                                <button class="btn btn-outline-danger btn-block mb-2" :disabled="tcpplusSyncing || tcpplusTerminating" @click.prevent="terminateInTCPPlus">
                                    {{ tcpplusTerminating ? 'Terminating…' : 'Terminate in TCP+' }}
                                </button>
                                <div v-if="tcpplusVisibleResults.length" class="mt-2 small">
                                    <div v-for="(r, idx) in tcpplusVisibleResults" :key="idx" class="mb-2 p-2 border rounded">
                                        <div><strong>{{ r.integration_label || ('Integration ' + r.integration_id) }}</strong></div>
                                        <div v-if="r.employee_created" class="text-success">Created TC+ Employee record</div>
                                        <div v-if="r.suspended" class="text-warning">Marked TC+ Employee as suspended</div>
                                        <div v-if="r.created && r.created.length">
                                            Created EJCs: <code v-for="(c, i) in r.created" :key="i">{{ c.jobCodeId }}<span v-if="c.isDefault"> (default)</span>{{ i < r.created.length - 1 ? ', ' : '' }}</code>
                                        </div>
                                        <div v-if="r.updated && r.updated.filter(u => u.updates && Object.keys(u.updates).length).length">
                                            Updated EJCs: <code v-for="(u, i) in r.updated.filter(u => u.updates && Object.keys(u.updates).length)" :key="i">{{ u.jobCodeId }}{{ i < r.updated.filter(u => u.updates && Object.keys(u.updates).length).length - 1 ? ', ' : '' }}</code>
                                        </div>
                                        <div v-if="r.deactivated && r.deactivated.length">
                                            Deactivated EJCs: <code v-for="(d, i) in r.deactivated" :key="i">{{ d.jobCodeId }}{{ i < r.deactivated.length - 1 ? ', ' : '' }}</code>
                                        </div>
                                        <div v-for="(w, i) in r.warnings" :key="'w' + i" class="text-warning">⚠ {{ w }}</div>
                                        <div v-for="(e, i) in r.errors" :key="'e' + i" class="text-danger">✗ {{ e }}</div>
                                    </div>
                                </div>

                                <hr v-if="tcpplusDataUpdates.length">
                                <div v-if="tcpplusDataUpdates.length">
                                    <ul class="list-unstyled m-0 text-small">
                                        <li class="d-none d-sm-block">
                                            <div class="form-row align-items-baseline py-hf">
                                                <div class="col-5"><h5 class="theading m-0">Date & Time</h5></div>
                                                <div class="col-4"><h5 class="theading m-0">Action</h5></div>
                                                <div class="col-3"><h5 class="theading m-0">Status</h5></div>
                                            </div>
                                        </li>
                                        <li class="boxed-li-bottom" v-for="updateObj in tcpplusDataUpdates" :key="updateObj.id">
                                            <div class="form-row align-items-baseline py-hf">
                                                <div class="col-12 col-sm-5"><span>{{ $filters.datetimeshort(updateObj.created_on) }}</span></div>
                                                <div class="col-12 col-sm-4"><span>{{ updateObj.action_pretty }}</span></div>
                                                <div class="col-12 col-sm-3">
                                                    <a href @click.prevent="posDataUpdateObj = updateObj; POSDataStatusUpdateModalOpen = true;">{{ updateObj.status_pretty }}</a>
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <pos-push-card
                            v-if="showPosCard('flexepos')"
                            :client="client"
                            :employee="employee"
                            pos-code="flexepos"
                            title="FlexePOS"
                            :pos-data-updates="posDataUpdates"
                            @show-status="updateObj => { posDataUpdateObj = updateObj; POSDataStatusUpdateModalOpen = true }"
                        />

                        <pos-push-card
                            v-if="showPosCard('synergy')"
                            :client="client"
                            :employee="employee"
                            pos-code="synergy"
                            title="SynergySuite"
                            :pos-data-updates="posDataUpdates"
                            @show-status="updateObj => { posDataUpdateObj = updateObj; POSDataStatusUpdateModalOpen = true }"
                        />

                        <pos-push-card
                            v-if="showPosCard('crunchtime')"
                            :client="client"
                            :employee="employee"
                            pos-code="crunchtime"
                            title="CrunchTime"
                            :pos-data-updates="posDataUpdates"
                            :terminate-endpoint="canTerminateInCrunchTime() ? 'pos-actions/crunchtime-terminate' : ''"
                            terminate-confirm="Terminate this employee in CrunchTime? This sets their CrunchTime record to Terminated. It does not change payroll."
                            @show-status="updateObj => { posDataUpdateObj = updateObj; POSDataStatusUpdateModalOpen = true }"
                        />

                    </div>
                </div>

                <!-- CHANGE HISTORY Tab Pane -->
                <div class="tab-pane flex-body body-scroll-x" :class="{'show active': activeTab == 'changelogs'}" id="tabContentHistory" role="tabpanel" aria-labelledby="tabLinkHistory" v-if="$permissions.hasRolesInCompany(client.id, employee.company_id, $store.state.security_roles.CHANGE_HISTORY_ROLES)">
                    <change-history :client="client" :changelogs="changelogs" :active-changelog-id="activeChangelogId" />
                </div>

                <!-- ADMIN Tab Pane -->
                <div class="tab-pane flex-body" id="tabContentAdmin" role="tabpanel" aria-labelledby="tabLinkAdmin" v-if="employee">
                    <div class="tab-pane pb-2" role="tabpanel">

                        <div v-if="onboardingApplicationId">
                            <div class="form-row mb-3">
                                <div class="col-6">
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminOnboarding()">Onboard as Employee</button>

                                    <!--
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminRepeatOnboardingStep('wotc')">WOTC</button>
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminRepeatOnboardingStep('hr-info')">HR Info</button>
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminRepeatOnboardingStep('address')">Address</button>
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminRepeatOnboardingStep('eeo-info')">EEO Information</button>
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminRepeatOnboardingStep('forms')">Forms</button>
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminRepeatOnboardingStep('direct-deposit')">Direct Deposit</button>
                                    -->
                                </div>

                                <div class="col-6">
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="getAOSData()">View Payroll Data</button>
                                </div>
                            </div>

                            <div class="form-row mb-3">
                                <div class="col-6">
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminClearSchoolDistrict()">Clear School District</button>
                                </div>
                                <div class="col-6">
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="AdminEVerifyUpdateCaseModalOpen = true;" @close="AdminEVerifyUpdateCaseModalOpen = false">Update E-Verify Case</button>
                                </div>
                            </div>

                            <div class="form-row mb-3">
                                <div class="col-6">
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminClearI9()">Clear I-9</button>
                                </div>
                                <div class="col-6">
                                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="adminClearEVerify()">Clear E-Verify</button>
                                </div>
                            </div>

                            <div class="my-2" v-if="aosData">
                                <pre><code>{{ aosData }}</code></pre>
                            </div>

                            <admin-onboarding-modal
                                ref="adminOnboardingModal"
                                :employee="employee"
                                :client="client"
                                :repeat-step="adminOnboardingRepeatStep"
                                @updated="$emit('updated', $event)"
                                @documents-updated="onOnboardingDocumentsUpdated"
                                @close="adminOnboardingRepeatStep = null"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <edit-employee-modal ref="editModal" :employee="employee" :client="client" @updated="onUpdate" v-if="canEditAOSHRInfo && !employee.hr_info_approved_on && editEmployeeModalOpened" @close="editEmployeeModalOpened = false" />

            <edit-email-modal
                :email="employee.email"
                :full-name="employee.full_name"
                :client="client"
                :employee-id="employeeId"
                :onboarding-application-id="onboardingApplicationId"
                @updated="onUpdate"
                v-if="editEmailModalOpened"
                @close="editEmailModalOpened = false"
            />

            <employee-terminate-modal
                :client="client"
                :employee="employee"
                v-if="employee && EmployeeTerminateModalOpen"
                @close="EmployeeTerminateModalOpen = false"
            />

            <pos-hire-modal
                :client="client"
                :employee="employee"
                v-if="employee && POSHireModalOpen"
                @close="POSHireModalOpen = false"
            />

            <pos-job-pay-modal
                :client="client"
                :employee="employee"
                v-if="employee && POSJobPayModalOpen"
                @close="POSJobPayModalOpen = false"
            />

            <pos-terminate-modal
                :client="client"
                :employee="employee"
                v-if="employee && POSTerminateModalOpen"
                @close="POSTerminateModalOpen = false"
            />

            <pos-data-update-status-modal
                :client="client"
                :employee="employee"
                :updateObj="posDataUpdateObj"
                v-if="posDataUpdateObj && POSDataStatusUpdateModalOpen"
                @close="POSDataStatusUpdateModalOpen = false"
            />

            <admin-e-verify-update-case-modal
                :client="client"
                :employee="employee"
                v-if="employee && AdminEVerifyUpdateCaseModalOpen"
                @close="AdminEVerifyUpdateCaseModalOpen = false"
            />

        </slot>

        <slot v-if="!employee || loading">
            <div class="loader loader-sidebar">
                <div class="spinner">
                <div class="lds-grid">
                    <div></div><div></div><div></div>
                    <div></div><div></div><div></div>
                    <div></div><div></div><div></div>
                </div>
                </div>
            </div>
        </slot>

    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'

// Header
import UnlockWidget from '@/components/UnlockWidget'
// Main tab
import AOSEmployeeDetails from './AOSEmployeeDetails/EmployeeDetails'
import EditEmployeeModal from './AOSEmployeeDetails/EditEmployeeModal'
import EmployeeTerminateModal from './AOSEmployeeDetails/EmployeeTerminateModal'
import EditEmailModal from '@/components/EditEmployeeEmailModal'
import EmployeeInfoWidget from './Main/EmployeeInfoWidget'
import EmployeeContactInfoWidget from './Main/EmployeeContactInfoWidget'
import EmployeeACAWidget from './Main/EmployeeACAWidget'
// Change History tab
import ChangeHistory from '@/components/ChangeHistory'
// Warnings tab
import Warnings from '@/components/WarningsWidget'
import WarningsHeader from '@/components/WarningsHeaderWidget'
// User Access tab
import UserAccess from '@/components/UserAccess'
import PINResetModal from '@/components/PINResetModal'
// POS tab
import POSHireModal from './pos/POSHireModal'
import POSJobPayModal from './pos/POSJobPayModal'
import POSTerminateModal from './pos/POSTerminateModal'
import POSDataUpdateStatusModal from './pos/POSDataUpdateStatusModal'
import POSPushCard from './pos/POSPushCard'
import { canTerminateInPos, posCardShows } from './pos/pos-push-card-helpers'
import { visibleTCPPlusResults, visibleTCPPlusUpdates } from './tcpplusResults'
// Admin tab
import AdminOnboardingModal from './Admin/AdminOnboardingModal'
import AdminEVerifyUpdateCaseModal from './Admin/AdminEVerifyUpdateCaseModal'
// Misc HR tab
import Approval from './MiscHR/Approval'
import OnboardingWidget from './MiscHR/OnboardingWidget'
import ManagersWidget from './MiscHR/ManagersWidget'
import EmergencyContactsWidget from './MiscHR/EmergencyContactsWidget'
import CheckHistoryWidget from './MiscHR/CheckHistoryWidget'
import EarningsStatementHistoryWidget from './MiscHR/EarningsStatementHistoryWidget'
import TaxDocumentsWidget from './MiscHR/TaxDocumentsWidget'
import EVerifyWidget from './MiscHR/EVerifyWidget'
import OtherCompanyRecordsWidget from './OtherCompanyRecordsWidget'
// HR Docs tab
import HRDocumentsTab from './HRDocuments/HRDocumentsTab'
// Payroll tab
import JobsWidget from './JobsWidget'
import OtherBenefitDeductionsWidget from './OtherBenefitDeductionsWidget'
import MiscDeductionsWidget from './MiscDeductionsWidget'
import RetirementLoansWidget from './RetirementLoansWidget'
import TaxWithholdingsWidget from './TaxWithholdingsWidget'
import IWOWidget from './IWOWidget'
import DeferredCompWidget from './DeferredCompWidget'
import InsurancePlansWidget from './InsurancePlansWidget'
import TimeOffWidget from './TimeOffWidget'
// Other
import exampleSidebarData from '@/components/Tours/example-employee-sidebar.json'

export default {
    components: {
        Sidebar, UnlockWidget,
        AOSEmployeeDetails, EditEmployeeModal, EditEmailModal, EmployeeTerminateModal,
        EmployeeInfoWidget, EmployeeContactInfoWidget, EmployeeACAWidget,
        ChangeHistory,
        Warnings, WarningsHeader,
        UserAccess, PinResetModal: PINResetModal,
        PosTerminateModal: POSTerminateModal, PosHireModal: POSHireModal, PosJobPayModal: POSJobPayModal, PosDataUpdateStatusModal: POSDataUpdateStatusModal,
        PosPushCard: POSPushCard,
        AdminOnboardingModal, AdminEVerifyUpdateCaseModal,
        Approval, OnboardingWidget, ManagersWidget, EmergencyContactsWidget, CheckHistoryWidget, EarningsStatementHistoryWidget, TaxDocumentsWidget, EVerifyWidget, OtherCompanyRecordsWidget,
        HRDocumentsTab,
        JobsWidget, OtherBenefitDeductionsWidget, MiscDeductionsWidget, RetirementLoansWidget, TaxWithholdingsWidget, IWOWidget, DeferredCompWidget,
        InsurancePlansWidget, TimeOffWidget,
    },
    props: ['employeeId', 'onboardingApplicationId', 'client', 'tab', 'section', 'itemId', 'expandedItemsSubsection', 'flashCompany' ],
    computed: {
        isEmployee() {
            return this.employee?.is_employee
        },
        tcpplusDataUpdates() {
            // Hide no-op reconcile rows (nothing created/updated/deactivated,
            // no warnings/errors) so the history table only lists meaningful
            // events. Covers legacy no-op rows still in the DB.
            return visibleTCPPlusUpdates(
                (this.posDataUpdates || []).filter(u => u.pos === 'tcpplus'),
            )
        },
        // Hide no-op syncs (nothing created/updated/deactivated, no warnings/errors).
        tcpplusVisibleResults() {
            return visibleTCPPlusResults(this.tcpplusLastResult)
        },
        altametricsDataUpdates() {
            // Everything that isn't owned by a single-action POS card — historically
            // this card showed all POS updates.
            const ownedElsewhere = ['tcpplus', 'flexepos', 'synergy', 'crunchtime']
            return (this.posDataUpdates || []).filter(u => !ownedElsewhere.includes(u.pos))
        },
        userIsAdmin() {
            return this.$store.state.user.is_superuser
        },
        showPOSTab() {
            if (!this.employee) return false
            const wl = this.employee.work_location
            return ['lc_alt_api', 'tcpplus', 'flexepos', 'synergy', 'crunchtime']
                .some(posCode => posCardShows(this.client, posCode, wl))
        },
        warningCount() {
            if (!this.employee) {return 0}
            return this.employee.warnings.filter((w) => {return !w.resolved_on && w.is_resolvable}).length
        },
        showEVerifyWidget() {
            return this.employee.company.everify_enabled && this.employee.everify_required
        },
        unresolvedWarnings() {
            if (!this.employee) {return []}
            return this.employee.warnings.filter(w => {
                const isDismissed = (this.$store.state.user.acknowledged_warning_ids.indexOf(w.id) >= 0)
                return (!isDismissed && !w.is_resolved)
            })
        },
        resolvedWarnings() {
            if (!this.employee) {return []}
            return this.employee.warnings.filter(w => {
                return w.is_resolved
            })
        },
        dismissedWarnings() {
            if (!this.employee) {return []}
            return this.employee.warnings.filter(w => {
                const isDismissed = (this.$store.state.user.acknowledged_warning_ids.indexOf(w.id) >= 0)
                return isDismissed && !w.is_resolved
            })
        },
        canEditAOSHRInfo() {
            if (!this.onboardingApplicationId) {return false}

            return !(this.employee.status == 'hired' || this.employee.status == 'canceled')
        },
        i9CompletedLateWarnings() {
            const result = []
            const warnings = this.employee.warnings.filter(w => w.warning_type == 'i9-completed-late')
            warnings.forEach(w => {
                const o = this.onboardingApplications.find(o => o.id == w.onboarding_application_id)
                if (o) {
                    const i9 = o.forms.find(f => f.form_designation == 'i9')
                    result.push({
                        warning: w,
                        onboardingApplication: o,
                        i9: i9,
                        date: i9.employer_form_data.date_hired
                    })
                }
            })
            return result
        },
        otherBenefitClientDeductionTypes() {
            return this.clientDeductionTypes.filter(d => d.deduction_type.category == 'other-benefits')
        },
        miscClientDeductionTypes() {
            return this.clientDeductionTypes.filter(d => d.deduction_type.category == 'misc')
        },
        // NB: no retirement-loan list here. Loans are backed by EmployeeMiscDeduction,
        // but their deduction type comes from the deferred comp plan the loan was drawn
        // against rather than from a picker, so RetirementLoansWidget has nothing to
        // filter. Their types stay out of miscClientDeductionTypes either way: the two
        // categories never overlap.
    },
    mounted() {
        this.getData()
    },
    data() {
        return {
            loading: false,
            employee: null,
            approvalMode: false,
            changelogs: [],
            editEmployeeModalOpened: false,
            editEmailModalOpened: false,
            EmployeeTerminateModalOpen: false,
            tcpplusSyncing: false,
            tcpplusTerminating: false,
            tcpplusLastResult: null,
            AdminEVerifyUpdateCaseModalOpen: false,
            POSHireModalOpen: false,
            POSJobPayModalOpen: false,
            POSTerminateModalOpen: false,
            POSDataStatusUpdateModalOpen: false,
            adminOnboardingRepeatStep: null,
            aosData: null,

            activeOnboardingCount: 0,
            onboardingApplications: [],
            managers: {},
            employeeContacts: [],
            employeeDocuments: [],
            employeeDocumentTasks: [],
            emergencyContacts: [],
            taxForms: [],
            eVerifyCases: [],
            loginSessions: [],
            checks: [],
            taxStatuses: [],
            posDataUpdates: [],
            clientEarningTypes: [],
            clientDeductionTypes: [],
            positions: [],
            otherCompanyRecords: [],

            posDataUpdateObj: null,

            activeTab: this.tab,

            onboardingAppsExpanded: this.section == 'onboarding' || this.tab != 'misc',
            managersExpanded: this.section == 'managers' || this.tab != 'misc',
            contactsExpanded: this.section == 'contacts' || this.tab != 'misc',
            checksExpanded: this.section == 'checks' || this.tab != 'misc',
            taxDocsExpanded: this.section == 'tax-documents' || this.tab != 'misc',
            eVerifyExpanded: this.section == 'e-verify' || this.tab != 'misc',
            otherCompanyRecordsExpanded: this.section == 'other-company-records',

            activeCheckId: (this.section == 'checks') ? this.itemId : null,
            activeTaxDocumentId: (this.section == 'tax-documents') ? this.itemId : null,

            activeWarningId: (this.tab == 'warnings') ? this.itemId : null,
            activeChangelogId: (this.tab == 'changelogs') ? this.itemId : null,

            unresolvedWarningsExpanded: true,
            resolvedWarningsExpanded: true,
            dismissedWarningsExpanded: true,
        }
    },
    methods: {
        getData() {
            this.getClientEarningTypes()
            this.getClientDeductionTypes()
            this.getPositions()

            if (this.employeeId) {
                return this.getEmployee()
            }
            else if (this.onboardingApplicationId) {
                return this.getOnboardingApplication()
            }
        },
        getClientEarningTypes() {
            return this.$api.get(`/clients/${this.client.id}/earning-types`).then(resp => {
                this.clientEarningTypes = resp
            })
        },
        getClientDeductionTypes() {
        return this.$api.get(`/clients/${this.client.id}/deduction-types`).then(resp => {
                this.clientDeductionTypes = resp
            })
        },
        getPositions() {
            return this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.positions = resp
            })
        },
        getEmployee() {
            if (this.$store.state.activeTour == 'hr-suite') {
                this.employee = exampleSidebarData.employee
                this.employeeDocumentTasks = exampleSidebarData.doc_tasks
                this.employeeDocuments = exampleSidebarData.docs
                return
            }
            this.loading = true
            return this.$api.get(`/clients/${this.client.id}/employees/${this.employeeId}/misc-hr-info`).then(resp => {

                this.employee = resp.employee
                this.taxStatuses = resp.tax_statuses
                this.activeOnboardingCount = resp.active_onboarding_count
                this.managers = resp.managers
                this.employeeContacts = resp.managers
                this.employeeDocuments = resp.docs
                this.employeeDocumentTasks = resp.doc_tasks
                this.emergencyContacts = resp.emergency_contacts
                this.taxForms = resp.tax_forms
                this.eVerifyCases = resp.everify_cases
                this.loginSessions = resp.login_sessions
                this.checks = resp.checks
                this.changelogs = resp.changelogs
                this.onboardingApplications = resp.onboarding_applications
                this.posDataUpdates = resp.pos_data_updates
                this.otherCompanyRecords = resp.other_company_records || []
                this.loading = false
            }).catch(errors => {
                this.$bus.showError(errors.__all__)
                this.loading = false
            })
        },
        getOnboardingApplication() {
            this.loading = true
            return this.$api.get(`/clients/${this.client.id}/onboarding-applications/${this.onboardingApplicationId}/misc-hr-info`).then(resp => {
                this.employee = resp.onboarding_application
                this.activeOnboardingCount = 0  // XXX: is this correct?
                this.managers = resp.managers
                this.taxForms = []
                this.eVerifyCases = resp.everify_cases
                this.loginSessions = resp.login_sessions
                this.emergencyContacts = resp.emergency_contacts
                this.posDataUpdates = resp.pos_data_updates
                this.checks = []
                this.changelogs = resp.changelogs
                this.loading = false
            }).catch(errors => {
                this.$bus.showError(errors.__all__)
                this.loading = false
            })
        },
        resetPIN() {
            this.$refs.pinResetModal.open()
        },
        resendActivationEmail() {
            if (!this.onboardingApplicationId) {return}

            this.$store.dispatch('START_LOADING')
            this.$api.post(`/clients/${this.client.id}/onboarding-applications/${this.employee.id}/invitation`, {}).then(() => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Email sent to ${this.employee.email}.`)
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(this.$bus.firstError(errors))
            })
        },
        expandAllMiscHr() {
            this.onboardingAppsExpanded = true
            this.managersExpanded = true
            this.contactsExpanded = true
            this.checksExpanded = true
            this.taxDocsExpanded = true
        },
        collapseAllMiscHr() {
            this.onboardingAppsExpanded = false
            this.managersExpanded = false
            this.contactsExpanded = false
            this.checksExpanded = false
            this.taxDocsExpanded = false
        },
        onUpdate(emp) {
            if (this.approvalMode && (this.employee.status != emp.status) && (emp.status == 'hired')) {
                this.approvalMode = false
            }

            if (this.editEmailModalOpened) {
                this.editEmailModalOpened = false
            }

            if (emp) {
                Object.assign(this.employee, emp)
            }

            this.$nextTick(() => this.getData())
            this.$emit('updated', this.employee)
        },
        onOnboardingDocumentsUpdated(app) {
            Object.assign(this.employee, app)
        },
        onEmergencyContactsUpdate(resp) {
            this.emergencyContacts = resp.emergency_contacts
            this.$emit('updated', this.employee)
        },
        onWarningsUpdate() {
            this.getData().then(() => {
                this.$emit('updated', this.employee)
            })
        },
        POSActionUpdateGeneral() {
            if (! window.confirm("This will update the employee's general information in Altametrics. Are you sure?")) {
                return;
            }

            const url = `/clients/${this.client.id}/employees/${this.employee.id}/pos-actions/update-general`;
            this.$store.dispatch('START_LOADING')
            this.$api.post(url, {}).then(() => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo("Sent to POS", "Sent to Altametrics. This may take several minutes.")
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        POSActionHire() {
            this.POSHireModalOpen = true
        },
        syncToTCPPlus() {
            this.tcpplusSyncing = true
            const url = `/clients/${this.client.id}/employees/${this.employee.id}/pos-actions/tcpplus-sync`
            this.$api.post(url, {}).then(resp => {
                this.tcpplusSyncing = false
                this.tcpplusLastResult = (resp && resp.results) || []
                this._summarizeTCPPlusResults(this.tcpplusLastResult, 'sync')
            }).catch(errors => {
                this.tcpplusSyncing = false
                this.$bus.showError(errors.__all__ || ['Sync failed. Check the sync log.'])
            })
        },
        terminateInTCPPlus() {
            if (!window.confirm("Deactivate this employee's EJCs in TCP+? If they are terminated everywhere in this client, the TCP+ Employee record will also be suspended.")) {
                return
            }
            this.tcpplusTerminating = true
            const url = `/clients/${this.client.id}/employees/${this.employee.id}/pos-actions/tcpplus-terminate`
            this.$api.post(url, {}).then(resp => {
                this.tcpplusTerminating = false
                this.tcpplusLastResult = (resp && resp.results) || []
                this._summarizeTCPPlusResults(this.tcpplusLastResult, 'terminate')
            }).catch(errors => {
                this.tcpplusTerminating = false
                this.$bus.showError(errors.__all__ || ['Terminate failed. Check the sync log.'])
            })
        },
        _summarizeTCPPlusResults(results, mode) {
            const verb = mode === 'terminate' ? 'terminate' : 'sync'
            if (!results || !results.length) {
                this.$bus.showInfo(`TCP+ ${verb}`, "No active TCP+ integrations on this client.")
                return
            }
            const totalCreated = results.reduce((n, r) => n + ((r.created || []).length), 0)
            const totalUpdated = results.reduce((n, r) => n + ((r.updated || []).filter(u => u.updates && Object.keys(u.updates).length).length), 0)
            const totalDeactivated = results.reduce((n, r) => n + ((r.deactivated || []).length), 0)
            const allErrors = results.flatMap(r => r.errors || [])
            const allWarnings = results.flatMap(r => r.warnings || [])
            const summary = `${totalCreated} created, ${totalUpdated} updated, ${totalDeactivated} deactivated`
            if (allErrors.length) {
                this.$bus.showError([`TCP+ ${verb} reported ${allErrors.length} error(s): `, ...allErrors])
            } else if (allWarnings.length) {
                this.$bus.showInfo(`TCP+ ${verb} complete (with warnings)`, summary)
            } else {
                this.$bus.showInfo(`TCP+ ${verb} complete`, summary)
            }
        },
        canTerminateInCrunchTime() {
            return canTerminateInPos(
                this.client,
                'crunchtime',
                this.$store.state.user.is_superuser,
                this.$permissions.hasRolesInCompany(this.client.id, this.employee.company_id, this.$store.state.security_roles.POS_TERMINATE_ROLES),
            )
        },
        showPosCard(posCode) {
            if (!this.employee) return false
            return posCardShows(this.client, posCode, this.employee.work_location)
        },
        POSActionHireNotAllowed() {
            window.alert("Employee is terminated.")
        },
        POSActionUpdateJobPay() {
            this.POSJobPayModalOpen = true
        },
        POSActionTerminate() {
            this.POSTerminateModalOpen = true
        },
        openEditEmployeeModal() {
            this.editEmployeeModalOpened = true
        },
        openEditEmailModal() {
            this.editEmailModalOpened = true
        },
        actionTerminate() {
            this.EmployeeTerminateModalOpen = true
        },
        getAOSData() {
            const url = `/admin/onboarding-applications/${this.employee.id}/aos-data`
            this.$store.dispatch('START_LOADING')
            this.$api.get(url).then(resp => {
                this.aosData = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        adminClearSchoolDistrict() {
            if (! window.confirm("This will clear the school district in this onboarding. Are you sure?")) {
                return;
            }

            const url = `/admin/onboarding-applications/${this.employee.id}/clear-school-district`
            this.$store.dispatch('START_LOADING')
            this.$api.post(url, {}).then(() => {
                this.$bus.showInfo("Cleared school district from onboarding.")
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        adminClearI9() {
            if (! window.confirm("This will clear the employer side of the I-9 in this onboarding. Are you sure?")) {
                return;
            }

            const url = `/admin/onboarding-applications/${this.employee.id}/clear-i9`
            this.$store.dispatch('START_LOADING')
            this.$api.post(url, {}).then(() => {
                this.$bus.showInfo("Cleared employer I-9.")
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        adminClearEVerify() {
            if (! window.confirm("This will delete any E-Verify cases in GetMyPayStub. Are you sure?\n\nNote that you still need to close any open cases in E-Verify itself.")) {
                return;
            }

            const url = `/admin/onboarding-applications/${this.employee.id}/clear-everify`
            this.$store.dispatch('START_LOADING')
            this.$api.post(url, {}).then(() => {
                this.$bus.showInfo("Deleted E-Verify cases in GetMyPayStub - close any open cases in E-Verify.")
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        adminOnboarding() {
            if (! window.confirm("This will reset the employee's onboarding. Are you sure?")) {
                return;
            }

            const url = `/admin/onboarding-applications/${this.employee.id}/reset`
            this.$store.dispatch('START_LOADING')
            this.$api.post(url, {}).then(() => {
                return this.$router.push({name: 'admin-onboarding', params: {appId: this.employee.id}})
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        adminRepeatOnboardingStep(step) {
            this.adminOnboardingRepeatStep = step
            this.$refs.adminOnboardingModal.open()
        },
        scrollTop(offset) {
            this.$refs.sidebar.scrollTop(offset)
        }
    },
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/globals";

@keyframes company-flash {
    0%   { background-color: color("yellow2-light"); color: color("yellow2-dark"); }
    40%  { background-color: color("yellow2-light"); color: color("yellow2-dark"); }
    100% { background-color: transparent; }
}

.company-flash {
    animation: company-flash 2.5s ease-out 1;
    display: inline-block;
    padding: 0.2em 0.6em;
    margin: -0.2em -0.6em;
    border-radius: 3px;
}
</style>
